import {
  AcillaryResponseInt,
  AncillariesResponseInt,
  BaggageAndServicesInt,
  CheckedBagsBenefitsInt,
  CurrencyAncillaryResponseInt,
  EmitTicketAncillaryRequestInt,
  PaymentCurrencyInt,
  PaymentFlightDetailInt,
  PaymentPassengerInt,
  PaymentPccInt,
  PaymentProductInt,
  PaymentTaxDetailInt,
  ResponseSeatInt,
  UpgradeAncillaryInt,
  UpgradeResponseInt,
} from '@app/interfaces/micro/ancillaries.interface';
import { BenefitBySegmentRequest } from '@app/interfaces/micro/benefit-by-segment.interface';
import { Reservation } from '@app/models/micro/reservation.model';
import {
  UpgradeSegmentOption,
  UpgradeReceiptItem,
  AncillariesV2Response,
} from '@app/interfaces/micro/upgrade.interface';
import moment from 'moment';
import {
  CHECKIN_STATUS,
  PAX_TYPE,
  StatusBag,
  SeatPassangerEstatus,
  TypeBagsWeight,
  FARES_FAMILY,
  PREMIER_FARE_FAMILIES,
  UPGRADE_CONSTANTS,
  DEFAULT_CURRENCY_CODE,
  OperatingCarrier,
} from '@app/enums';
import { FlightSummary, Passenger } from '@app/models/micro';
import { BagsPassangerInt } from '@app/interfaces/micro/bags.interface';
import { BagPassangerModel } from '@app/models/micro/bags.model';
import { PaymentInfoInt } from '@app/interfaces';
import { SEAT_ANCILLARY_CODE } from '@app/utils/constants/generals';
import { hasPaidChubbAncillary } from '@app/utils/functions/chubb-remarks.utils';

const DEFAULT_CONSTANTS = {
  CURRENCY_CODE: 'MXN',
  COBRAND_CARD: 'AMEXPLT',
  REQUEST_FROM: 'Kiosk Self-Service',
  BOOKING_CABIN: 'Y',
  REMAINING_TIME: 1,
  ROLLBACK: false,
  REQUEST_FROM_FE: 'FE',
  TAX_CODE: 'MXA',
} as const;

/**
 * Mapea el groupCode del producto al campo `type` de POST /pc/emit-ticket-ancillary.
 * - BG (maletas, sobrepeso) → ITINERARY_PART
 * - TS / IE (travel services, seguros) → ITINERARY
 * - SA (asientos) y UP (upgrade, se cobra por segmento) → SEGMENT
 * Cualquier otro grupo cae en ITINERARY_PART como default.
 * Las maletas y el carry-on se fuerzan a ITINERARY_PART en getPaymentProducts: el carry-on
 * (07X) llega con groupCode TS y es un EMD Associated que requiere vuelo asociado.
 */
const PAYMENT_PRODUCT_TYPE_BY_GROUP: Record<string, string> = {
  BG: 'ITINERARY_PART',
  SA: 'SEGMENT',
  UP: 'SEGMENT',
  TS: 'ITINERARY',
  IE: 'ITINERARY',
  '99': 'ITINERARY_PART',
};

/** Moneda de origen de un producto por cobrar (maleta, asiento, seguro, upgrade). */
interface PaymentSourceCurrencyInt {
  currencyCode?: string;
  base?: unknown;
  totalTax?: unknown;
  taxes?: unknown;
  total?: unknown;
  taxDetails?: unknown;
}

/** Producto por cobrar tal como vive en el pasajero (bagStack, seatsNew, Chubb, upgrade). */
interface PaymentSourceProductInt {
  commercialName?: string;
  nameDescription?: string;
  name?: string;
  rficSubcode?: string;
  code?: string;
  owningCarrierCode?: string;
  emdType?: unknown;
  groupCode?: string;
  group?: string;
  airExtraItemNumber?: unknown;
  id?: unknown;
  segmentIndicator?: string;
  rficCode?: string;
  rfic?: string;
  ssrCode?: string;
  vendor?: string;
  quantity?: unknown;
  legCode?: string;
  segmentCode?: string;
  currency?: PaymentSourceCurrencyInt;
  currencyPerLeg?: { currency?: PaymentSourceCurrencyInt }[];
}

const REDEMPTION_TYPES = {
  tier: 'TIER',
  cobrand: 'COBRAND',
  corporate: 'CORPORATE',
} as const;

const BAG_WEIGHT_MAPPING = {
  10: TypeBagsWeight._10KG,
  15: TypeBagsWeight._15KG,
  25: TypeBagsWeight._25KG,
  32: TypeBagsWeight._32KG,
} as const;

const NAMES_BAGS = {
  [TypeBagsWeight._10KG]: 'ST BAG UP TO 55LB 10KG',
  [TypeBagsWeight._15KG]: 'ST BAG UP TO 55LB 15KG',
  [TypeBagsWeight._25KG]: 'ST BAG UP TO 55LB 25KG',
  [TypeBagsWeight._32KG]: 'ST BAG UP TO 55LB 32KG',
} as const;

// Nombre comercial de respaldo por peso para el CARRY ON.
// Evita que la línea DESCRIPCION del recibo quede vacía cuando el bag
// llega sin `commercialName` (p. ej. carry-on con sobrepeso +5kg -> 15KG).
const CARRY_ON_NAMES_BY_WEIGHT = {
  [TypeBagsWeight._10KG]: 'CARRY ON UP TO 10KG',
  [TypeBagsWeight._15KG]: 'CARRY ON UP TO 15KG',
} as const;

// Devuelve el `commercialName` del bag o, si viene vacío, un nombre de
// respaldo derivado del peso. Garantiza que la DESCRIPCION del ticket
// nunca se envíe en blanco.
function bagDescription(bag: BagsPassangerInt, fallbackByWeight?: Record<number, string>): string {
  const name = (bag?.commercialName || '').trim();
  if (name) {
    return name;
  }
  return fallbackByWeight?.[bag?.typeBagWeight] || '';
}

// Total cobrado del carry-on. El precio puede venir en `currency.total` o, segun el
// flujo (BASIC/Main Light), solo en `currencyPerLeg[].currency.total`. Se lee con el
// mismo fallback que usa el request de pago para que el recibo no quede en 0.
function carryOnTotal(bag: BagsPassangerInt): number {
  return bag?.currency?.total ?? bag?.currencyPerLeg?.[0]?.currency?.total ?? 0;
}

export class AncillariesMapper {
  //#region Funciones privadas

  //Mapea un segmento de vuelo a la estructura requerida por la API
  private static mapSegmentToApiFormat(segment: any, reservation?: Reservation) {
    return {
      segmentCode: segment.segmentCode,
      remainingSegmentTimeToCheckin: segment.remainingSegmentTimeToCheckin,
      aircraftType: segment.aircraftType,
      origin: segment.departureAirport,
      destination: segment.arrivalAirport,
      marketingCarrier: segment.marketingCarrier,
      operatingCarrier: segment.operatingCarrier,
      operatingFlightCode: segment.operatingFlightCode,
      marketingFlightCode: segment.marketingFlightCode,
      departureDate: segment.departureDateTime,
      arrivalDate: segment.arrivalDateTime,
      farebasis: segment.farebasis,
      fareFamily: segment.fareFamily === 'BASIC' ? 'MAIN_BASIC' : segment.fareFamily,
      bookingClass: segment.bookingClass,
      bookingCabin: segment.cabinClass,
      segmentNumber: segment.segmentNumber,
      segmentEntity: segment.segmentEntity,
      departureAirportTimeZoneId: segment.departureAirportTimeZoneId,
      ...(reservation && { segmentRegion: reservation.flightSummary.legRegion }),
    };
  }

  //Construye la estructura de currency estándar
  private static buildCurrencyStructure(currency?: any) {
    return {
      currencyCode: currency?.currencyCode || DEFAULT_CONSTANTS.CURRENCY_CODE,
      base: currency?.base || 0,
      taxDetails: currency?.taxDetails || [],
      totalTax: currency?.totalTax || 0,
      subTotal: currency?.subTotal || 0,
      total: currency?.total || 0,
      totalCPPoints: currency?.totalCPPoints || 0,
    };
  }

  //Mapea información básica de pasajero
  private static mapPassengerBasicInfo(passenger: Passenger) {
    return {
      nameNumber: passenger.nameRefNumber,
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      type: this.getPaxTypeShort(passenger.paxType),
      dateOfBirth: passenger.dateOfBirth,
    };
  }

  //Obtiene el tipo de peso de equipaje basado en el peso en kg
  private static getBagWeightType(weightKg: number): TypeBagsWeight {
    const normalizedWeight = Math.round(Number(weightKg));
    return BAG_WEIGHT_MAPPING[normalizedWeight] || TypeBagsWeight._25KG;
  }

  //Obtiene la cantidad de maletas incluidas segun el tipo de pasajero
  private static getCheckedBagsQuantity(
    checkedBags: CheckedBagsBenefitsInt,
    paxType: string
  ): number {
    const shortType = this.getPaxTypeShort(paxType);
    const quantityByType = checkedBags?.quantities?.[shortType];

    if (typeof quantityByType === 'number') {
      return quantityByType;
    }

    return checkedBags?.quantity ?? 0;
  }

  //Crea maletas de equipaje de mano
  private static createCarryOnBags(carryOn: any): BagsPassangerInt[] {
    const bags: BagsPassangerInt[] = [];
    const typeCarryon = this.getBagWeightType(carryOn.weightKg);

    for (let i = 0; i < carryOn.quantity; i++) {
      const ancillaryCarryOn = BagPassangerModel.builder()
        .withTypeBagWeight(typeCarryon)
        .withCurrency(undefined)
        .withOrder(1)
        .build();
      bags.push(ancillaryCarryOn);
    }
    return bags;
  }

  //Crea maletas incluidas
  private static createCheckedBags(
    checkedBags: CheckedBagsBenefitsInt,
    paxType: string,
    hasInfant: boolean,
    infantCheckedBaggage: any
  ): BagsPassangerInt[] {
    const bags: BagsPassangerInt[] = [];
    let order = 1;
    const type = this.getBagWeightType(checkedBags.weightKg);
    const quantity = this.getCheckedBagsQuantity(checkedBags, paxType);

    for (let i = 0; i < quantity; i++) {
      const commercialName = `${order}${NAMES_BAGS[type]}`;
      const ancillaryCheckedBag = BagPassangerModel.builder()
        .withTypeBagWeight(type)
        .withCommercialName(commercialName)
        .withCurrency(undefined)
        .withOrder(order)
        .build();

      bags.push(ancillaryCheckedBag);
      order++;
    }

    if (hasInfant && infantCheckedBaggage.quantity > 0) {
      const commercialName = `${order}${NAMES_BAGS[TypeBagsWeight._25KG]}`;
      const ancillaryCheckedBagInfant = BagPassangerModel.builder()
        .withTypeBagWeight(TypeBagsWeight._25KG)
        .withCommercialName(commercialName)
        .withCurrency(undefined)
        .withOrder(order)
        .build();
      bags.push(ancillaryCheckedBagInfant);
    }

    return bags;
  }

  /**
   * Ordena las maletas extra por orden comercial
   * @param extraBags Array de maletas a ordenar
   * @returns Array ordenado de maletas con propiedad order
   */
  private static orderExtraBag(extraBags: AcillaryResponseInt[]) {
    return extraBags
      .map((ancillary) => ({
        order: Number(ancillary.commercialName.substring(0, 1)) || 0,
        ...ancillary,
      }))
      .sort((a, b) => a.order - b.order);
  }

  //Mapea información de viajero para ancillaries request
  private static mapTravelerInfo(passengers: Passenger[]) {
    return passengers
      .filter((p) => p.isSelectedToCheckin || p.isSelectForBagagge)
      .map((passenger) => {
        const { nameRefNumber, lastName, firstName, dateOfBirth, passengerInfo, ticketInfo } =
          passenger;
        const { ffNumber, ffProgram, type } = passengerInfo;

        return {
          nameNumber: nameRefNumber,
          firstName,
          lastName,
          dateOfBirth,
          type,
          ticketNumber: ticketInfo?.eticket,
          loyaltyAccount: {
            frequentFlyerProgram: ffProgram || '',
            frequentFlyerNumber: ffNumber || '',
          },
        };
      });
  }

  //Mapea segmentos para ancillaries request
  private static mapSegmentsForAncillaries(segments: any[]) {
    return segments
      .filter((s) => s.windowSegmentStatus !== CHECKIN_STATUS.CLOSED)
      .map((segment) => {
        const flightNumber = segment.flightNumber.split(' ');
        const [, operatingFlightCode] = flightNumber;

        return {
          ...this.mapSegmentToApiFormat(segment),
          operatingFlightCode,
        };
      });
  }

  //Mapea información de asientos para un pasajero
  private static mapPassengerSeats(passenger: Passenger) {
    return passenger.seats.map((seat) => ({
      seatCode: seat?.seatCode,
      segmentCode: seat?.segmentCode,
      status: seat?.status,
      currency: seat?.currency,
    }));
  }

  //Mapea información de cupones para un pasajero
  private static mapPassengerCoupons(passenger: Passenger) {
    return (
      passenger?.ticketInfo?.coupons?.map((coupon) => ({
        couponNumber: coupon?.couponNumber,
        couponStatus: coupon?.couponStatus,
        segmentCode: coupon?.segmentCode,
      })) || []
    );
  }

  //Filtra segmentos que tienen asientos asignados
  // Incluye segmentos con asientos en seats, seatsNew, o el segmento del asiento que se está seleccionando
  private static filterSegmentsWithSeats(
    segments: any[],
    passengers: Passenger[],
    currentSeatSegmentCode?: string
  ) {
    return (
      segments?.filter((seg) => {
        // Verificar si algún pasajero tiene asiento en este segmento (seats o seatsNew)
        const hasSeatInSegment = passengers.some(
          (p) =>
            p.seats?.some((s) => s.segmentCode === seg.segmentCode) ||
            p.seatsNew?.some((s) => s.segmentCode === seg.segmentCode)
        );

        // Incluir también el segmento del asiento que se está seleccionando actualmente
        const isCurrentSeatSegment =
          currentSeatSegmentCode && seg.segmentCode === currentSeatSegmentCode;

        return hasSeatInSegment || isCurrentSeatSegment;
      }) || []
    );
  }
  //#endregion Funciones privadas

  //#region Funciones públicas

  /**
   * Genera request para obtener mapa de asientos
   * @param reservation Información de la reserva
   * @returns Objeto request para API de mapa de asientos
   */
  static seatMapRequest(reservation: Reservation) {
    // Solo enviar premierAvailable si hay segmentos que NO son Premier
    const activeSegments = reservation.segments.filter(
      (seg) => seg.windowSegmentStatus !== CHECKIN_STATUS.CLOSED
    );
    const hasNonPremierSegment = activeSegments.some(
      (seg) => !PREMIER_FARE_FAMILIES.includes(seg.fareFamily as FARES_FAMILY)
    );

    return {
      reservationCode: reservation.pnr,
      isStandBy: reservation.isStandByBooking,
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      isNewBF: reservation.isNewBF,
      legs: [
        {
          legCode: reservation.flightSummary.legCode,
          legRegion: reservation.flightSummary.legRegion,
          windowLegStatus: 'CKI',
          ...(hasNonPremierSegment && { premierAvailable: true }),
          segments: activeSegments.map((segment) => ({
            ...this.mapSegmentToApiFormat(segment, reservation),
          })),
        },
      ],
      passenger: reservation.passengers.map((pax) => ({
        firstName: pax.firstName,
        lastName: pax.lastName,
        passengerNameNumber: pax.nameRefNumber,
        paxType: this.getPaxTypeShort(pax.paxType),
        ssrs: (pax.ssrs || []).map((ssr) => ssr.code).join(','),
      })),
    };
  }

  /**
   * Procesa respuesta del mapa de asientos
   * @param response Respuesta de la API
   * @returns Secciones del mapa de asientos
   */
  static seatMapResponse(response: any) {
    return response?.sections || [];
  }

  /**
   * Genera request para obtener ofertas de upgrade Premier
   * @param reservation Información de la reserva
   * @returns Objeto request para API de upgrade
   */
  static upgradeRequest(reservation: Reservation) {
    // CVT20-14550: passengerNumbers debe contar solo los pasajeros que ocupan
    // asiento. El infante viaja en el regazo del adulto (no ocupa asiento propio),
    // por lo que incluirlo hacía que el backend recibiera N+1 pasajeros contra N
    // asientos Premier y NO devolviera oferta de upgrade. El infante SÍ se mantiene
    // en `travelerInfo` porque el backend lo necesita para tarificar/emitir.
    const seatOccupyingPassengers =
      reservation.passengers?.filter((p) => p?.paxType !== PAX_TYPE.INFANT).length || 0;

    return {
      reservationCode: reservation.pnr,
      passengerNumbers: String(seatOccupyingPassengers),
      legs: [
        {
          legCode: reservation.flightSummary.legCode,
          legRegion: reservation.flightSummary.legRegion,
          windowLegStatus: 'CKI',
          segments: reservation.segments
            .filter((seg) => seg.windowSegmentStatus !== CHECKIN_STATUS.CLOSED)
            .filter((seg) => !PREMIER_FARE_FAMILIES.includes(seg.fareFamily as FARES_FAMILY))
            .map((segment) => ({
              aircraftType: segment.aircraftType,
              arrivalDate: segment.arrivalDateTime,
              bookingClass: segment.bookingClass,
              bookingCabin: segment.cabinClass || DEFAULT_CONSTANTS.BOOKING_CABIN,
              destination: segment.arrivalAirport,
              departureDate: segment.departureDateTime,
              farebasis: segment.farebasis,
              fareFamily: segment.fareFamily === 'BASIC' ? 'MAIN_BASIC' : segment.fareFamily,
              marketingCarrier: segment.marketingCarrier,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              origin: segment.departureAirport,
              segmentCode: segment.segmentCode,
              sequence: segment.sequence,
            })),
        },
      ],
      travelerInfo: reservation.passengers.map((pax) => ({
        firstName: pax.firstName,
        lastName: pax.lastName,
        nameNumber: pax.nameRefNumber,
        dateOfBirth: pax.dateOfBirth,
        type: this.getPaxTypeShort(pax.paxType),
        ticketNumber: pax.ticketInfo?.eticket || '',
        loyaltyAccount: {
          frequentFlyerProgram: pax.passengerInfo?.ffProgram || '',
          frequentFlyerNumber: pax.passengerInfo?.ffNumber || '',
        },
      })),
    };
  }

  /**
   * Procesa respuesta de ofertas de upgrade Premier
   * @param response Respuesta de la API de upgrade
   * @returns Mapa de segmentCode → costo del upgrade
   */
  static upgradeResponse(
    response: UpgradeResponseInt
  ): Map<string, { total: number; currencyCode: string }> {
    const costMap = new Map<string, { total: number; currencyCode: string }>();
    const upgrades = response?.upgrades || [];

    for (const upgrade of upgrades) {
      for (const seg of upgrade.currencyPerSegment || []) {
        costMap.set(seg.segmentCode, {
          total: seg.currency?.total || 0,
          currencyCode: seg.currency?.currencyCode || 'MXN',
        });
      }
    }

    return costMap;
  }

  /**
   * Procesa respuesta de upgrade devolviendo la data completa por segmento,
   * necesaria para construir el payload de update-products.
   */
  static upgradeResponseFull(response: UpgradeResponseInt): Map<
    string,
    {
      total: number;
      currencyCode: string;
      offer: UpgradeAncillaryInt;
      currency: CurrencyAncillaryResponseInt;
    }
  > {
    const costMap = new Map<
      string,
      {
        total: number;
        currencyCode: string;
        offer: UpgradeAncillaryInt;
        currency: CurrencyAncillaryResponseInt;
      }
    >();
    const upgrades = response?.upgrades || [];

    for (const upgrade of upgrades) {
      for (const seg of upgrade.currencyPerSegment || []) {
        costMap.set(seg.segmentCode, {
          total: seg.currency?.total || 0,
          currencyCode: seg.currency?.currencyCode || 'MXN',
          offer: upgrade,
          currency: seg.currency,
        });
      }
    }

    return costMap;
  }

  /**
   * Genera request para agregar el upgrade como ancillary (PUT ac/ancillaries/v2/{PNR}).
   */
  static addUpgradeAncillaryRequest(
    reservation: Reservation,
    selectedSegments: UpgradeSegmentOption[],
    upgradeOffers: Map<
      string,
      { offer: UpgradeAncillaryInt; currency: CurrencyAncillaryResponseInt }
    >
  ) {
    const { flightSummary, passengers } = reservation;

    return {
      legs: [
        {
          legCode: flightSummary.legCode,
          legRegion: flightSummary.legRegion,
          segments: reservation.segments
            .filter((seg) => selectedSegments.some((ss) => ss.segmentCode === seg.segmentCode))
            .map((segment) => ({
              aircraftType: segment.aircraftType,
              arrivalDate: segment.arrivalDateTime,
              bookingCabin: segment.cabinClass || 'Y',
              bookingClass: segment.bookingClass,
              departureDate: segment.departureDateTime,
              destination: segment.arrivalAirport,
              fareFamily: segment.fareFamily,
              marketingCarrier: segment.marketingCarrier,
              marketingFlightCode: segment.operatingFlightCode,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              origin: segment.departureAirport,
              segmentCode: segment.segmentCode,
              sequence: segment.sequence || 1,
            })),
        },
      ],
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      // CVT20-14292: los infantes no ocupan asiento, no se les agrega el ancillary de upgrade.
      travelerInfo: passengers
        .filter(
          (p) => (p.checkinSelected || p.isSelectedToCheckin) && p.paxType !== PAX_TYPE.INFANT
        )
        .map((passenger) => {
          const ancillaries = selectedSegments.map((seg) => {
            const offerData = upgradeOffers.get(seg.segmentCode);
            const offer = offerData?.offer;
            const currency = offerData?.currency;

            return {
              ancillary: {
                currency: {
                  base: currency?.base || 0,
                  currencyCode: currency?.currencyCode || DEFAULT_CURRENCY_CODE,
                  subTotal: currency?.subTotal || currency?.total || 0,
                  taxDetails: currency?.taxDetails || [],
                  total: currency?.total || 0,
                  totalCPPoints: currency?.totalCPPoints || 0,
                  totalTax: currency?.totalTax || 0,
                },
                emdType: offer?.emdType || UPGRADE_CONSTANTS.EMD_TYPE,
                groupCode: offer?.groupCode || UPGRADE_CONSTANTS.GROUP_CODE,
                id: '',
                owningCarrierCode: offer?.owningCarrierCode || OperatingCarrier.AM,
                rficCode: offer?.rficCode || UPGRADE_CONSTANTS.RFIC_CODE,
                rficSubcode: offer?.rficSubcode || UPGRADE_CONSTANTS.RFIC_SUBCODE,
                ssrCode: offer?.ssrCode || UPGRADE_CONSTANTS.SSR_CODE,
              },
              legCode: flightSummary.legCode,
              quantity: 1,
              segmentCode: seg.segmentCode,
            };
          });

          return {
            nameNumber: passenger.nameRefNumber,
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            eticket: passenger.ticketInfo?.eticket || '',
            ancillaries,
          };
        }),
    };
  }

  static removeUpgradeAncillaryRequest(
    reservation: Reservation,
    ancillariesResponse: AncillariesV2Response,
    selectedSegments: UpgradeSegmentOption[]
  ) {
    const { flightSummary, passengers } = reservation;
    const travelerInfoResp = ancillariesResponse?.travelerInfo || [];

    const upgradeAncByKey = new Map<
      string,
      AncillariesV2Response['travelerInfo'][number]['ancillaries'][number]
    >();
    travelerInfoResp.forEach((traveler) => {
      (traveler.ancillaries || []).forEach((anc) => {
        if (anc.ancillary?.groupCode === UPGRADE_CONSTANTS.GROUP_CODE && anc.ancillary?.id) {
          upgradeAncByKey.set(`${traveler.nameNumber}_${anc.segmentCode}`, anc);
        }
      });
    });

    const travelerInfo = passengers
      .filter((p) => (p.checkinSelected || p.isSelectedToCheckin) && p.paxType !== PAX_TYPE.INFANT)
      .map((passenger) => {
        const ancillaries = (selectedSegments || [])
          .map((seg) => {
            const anc = upgradeAncByKey.get(`${passenger.nameRefNumber}_${seg.segmentCode}`);
            if (!anc) {
              return null;
            }
            return {
              ancillary: {
                id: anc.ancillary.id,
                rficSubcode: anc.ancillary.rficSubcode || UPGRADE_CONSTANTS.RFIC_SUBCODE,
                groupCode: anc.ancillary.groupCode || UPGRADE_CONSTANTS.GROUP_CODE,
                emdType: anc.ancillary.emdType || UPGRADE_CONSTANTS.EMD_TYPE,
                rficCode: anc.ancillary.rficCode || UPGRADE_CONSTANTS.RFIC_CODE,
                ssrCode: anc.ancillary.ssrCode || UPGRADE_CONSTANTS.SSR_CODE,
                owningCarrierCode: anc.ancillary.owningCarrierCode || OperatingCarrier.AM,
                currency: this.buildCurrencyStructure(anc.ancillary.currency),
              },
              legCode: anc.legCode || flightSummary.legCode,
              segmentCode: anc.segmentCode,
              quantity: 0,
            };
          })
          .filter((anc): anc is NonNullable<typeof anc> => anc !== null);

        return {
          nameNumber: passenger.nameRefNumber,
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          eticket: passenger.ticketInfo?.eticket || '',
          ancillaries,
        };
      })
      .filter((traveler) => traveler.ancillaries.length > 0);

    const upgradeSegmentCodes = new Set(
      travelerInfo.flatMap((t) => t.ancillaries.map((a) => a.segmentCode))
    );

    return {
      legs: [
        {
          legCode: flightSummary.legCode,
          legRegion: flightSummary.legRegion,
          segments: reservation.segments
            .filter((seg) => upgradeSegmentCodes.has(seg.segmentCode))
            .map((segment) => ({
              aircraftType: segment.aircraftType,
              arrivalDate: segment.arrivalDateTime,
              bookingCabin: segment.cabinClass || 'Y',
              bookingClass: segment.bookingClass,
              departureDate: segment.departureDateTime,
              destination: segment.arrivalAirport,
              fareFamily: segment.fareFamily,
              marketingCarrier: segment.marketingCarrier,
              marketingFlightCode: segment.operatingFlightCode,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              origin: segment.departureAirport,
              segmentCode: segment.segmentCode,
              sequence: segment.sequence || 1,
            })),
        },
      ],
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      travelerInfo,
    };
  }

  /**
   * Genera request para obtener servicios adicionales (ancillaries)
   * @param reservation Información de la reserva
   * @returns Objeto request para API de ancillaries
   */
  static getAncillariesRequest(reservation: Reservation) {
    // CI-21988: destructurar una reserva undefined lanzaba TypeError, y
    // `rawCreationDate.split()` reventaba si la reserva no traía creationDate.
    const {
      passengers,
      pnr,
      flightSummary,
      creationDate: rawCreationDate,
    } = reservation ?? ({} as Reservation);
    const creationDate = (rawCreationDate ?? '').split('T')[0];

    // CI-18515: Construir legCode del PRIMER SEGMENTO solamente
    // Para vuelos con conexión, el backend solo acepta el primer tramo
    const firstSegment = flightSummary?.segments?.[0];
    let legCodeFirstSegment = '';
    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      legCodeFirstSegment = `${firstSegment.departureAirport}_${firstSegment.arrivalAirport}_${departureDate}`;
    }

    // Fallback si no se puede construir
    if (!legCodeFirstSegment) {
      legCodeFirstSegment = flightSummary?.legCode || '';
    }

    return {
      reservationCode: pnr,
      creationDate,
      travelerInfo: this.mapTravelerInfo(passengers ?? []),
      legs: [
        {
          legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
          legRegion: flightSummary?.legRegion,
          windowLegStatus: flightSummary?.windowLegStatus,
          // CI-18515: Enviar SOLO el primer segmento
          segments: firstSegment
            ? [this.mapSegmentToApiFormat(firstSegment, reservation)]
            : this.mapSegmentsForAncillaries(flightSummary?.segments || []),
        },
      ],
    };
  }

  /**
   * Procesa respuesta de servicios adicionales disponibles
   * @param response Respuesta de la API con carryOn, extraBags y overweight
   * @param legCode Código del tramo (actualmente no utilizado)
   * @returns Objeto con passengers y overweight procesados
   */
  static getAncillariesResponse(response: AncillariesResponseInt, legCode: string) {
    if (!response) {
      throw new Error('Response is required for getAncillariesResponse');
    }

    const { carryOn, extraBags, overweight } = response;
    let passengers: any[] = [];

    if (extraBags?.length > 0) {
      const bags = this.orderExtraBag(extraBags);
      passengers = extraBags[0].applyTo.map((referenceNumber) => ({
        referenceNumber, // Fixed typo from refererNumbrer
        extraBags: bags,
        carryOn: [],
      }));
    }

    if (carryOn?.length > 0) {
      const bags = this.orderExtraBag(carryOn);
      if (passengers.length === 0) {
        passengers = carryOn[0].applyTo.map((referenceNumber) => ({
          referenceNumber, // Fixed typo from refererNumbrer
          carryOn: bags,
          extraBags: [],
        }));
      } else {
        passengers.forEach((passenger) => {
          passenger.carryOn = bags;
        });
      }
    }

    return {
      overweight,
      passengers,
    };
  }

  /**
   * Genera request para eliminar servicios adicionales
   * @param reservation Información de la reserva
   * @returns Objeto request para eliminar ancillaries
   */
  static removeAncillariesRequest(reservation: Reservation) {
    const { flightSummary, passengers } = reservation;

    // CI-18515: Construir legCode del PRIMER SEGMENTO solamente
    const firstSegment = flightSummary?.segments?.[0];
    let legCodeFirstSegment = '';
    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      legCodeFirstSegment = `${firstSegment.departureAirport}_${firstSegment.arrivalAirport}_${departureDate}`;
    }
    if (!legCodeFirstSegment) {
      legCodeFirstSegment = flightSummary?.legCode || '';
    }

    return {
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      travelerInfo: passengers.map((passenger) => ({
        ...this.mapPassengerBasicInfo(passenger),
        ancillaries: [
          ...passenger.bagStack,
          ...passenger.ancillaries,
          ...passenger.bagStackCarryon,
          // CVT20-14766: faltaba bagOfferCarryOnStack (oferta +5kg), su hold
          // nunca se liberaba al dar "Regresar" desde el carrito.
          ...(passenger.bagOfferCarryOnStack || []),
        ]
          .filter(
            (b) =>
              b.status === StatusBag.TO_PAY || b.status === StatusBag.ON_HOLD || b.isExtraWeight
          )
          // Solo incluir ancillaries que tienen un ID numérico del backend para evitar mandar ids generados localmente (bagStack, bagStackCarryon, bagOfferCarryOnStack)
          .filter((ancillary) => {
            const numericId = Number(ancillary.id);
            return !isNaN(numericId) && numericId > 0 && numericId < 1000;
          })
          .map((ancillary) => ({
            ancillary: {
              id: ancillary.id,
              rficSubcode: ancillary.rficSubcode || ancillary.code,
              groupCode: ancillary.groupCode,
              emdType: ancillary.emdType,
              rficCode: ancillary.rficCode,
              ssrCode: ancillary.ssrCode,
              owningCarrierCode: ancillary.owningCarrierCode,
              currency: this.buildCurrencyStructure(ancillary?.currency),
            },
            legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
            quantity: 0,
          })),
      })),
      legs: [
        {
          legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
          legRegion: flightSummary.legRegion,
          // CI-18515: Enviar SOLO el primer segmento
          segments: firstSegment
            ? [this.mapSegmentToApiFormat(firstSegment)]
            : flightSummary?.segments?.map((segment) => this.mapSegmentToApiFormat(segment)),
        },
      ],
    };
  }

  /**
   * Genera request para redimir servicios adicionales (tier, cobrand, corporate)
   * @param reservation Información de la reserva
   * @param passenger Información del pasajero
   * @param ancillary Servicio adicional a redimir
   * @param type Tipo de redención ('tier', 'cobrand', 'corporate')
   * @returns Objeto request para redimir ancillary
   */
  static redeemAncillariesRequest(
    reservation: Reservation,
    passenger: Passenger,
    ancillary: BagsPassangerInt | BagsPassangerInt[],
    type: string
  ) {
    const { flightSummary } = reservation;
    const ancillaries = Array.isArray(ancillary) ? ancillary : [ancillary];

    const firstSegment = flightSummary?.segments?.[0];
    let legCodeFirstSegment = '';
    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      legCodeFirstSegment = `${firstSegment.departureAirport}_${firstSegment.arrivalAirport}_${departureDate}`;
    }
    if (!legCodeFirstSegment) {
      legCodeFirstSegment = flightSummary?.legCode || '';
    }

    const mappedAncillaries = ancillaries.map((a) => {
      const currencyAncillary = a.currencyPerLeg?.find((c) => c.legCode === legCodeFirstSegment);
      const currency = currencyAncillary?.currency || a.currency;
      return {
        ancillary: {
          id: a.order || a.id,
          rficSubcode: a.rficSubcode,
          groupCode: a.groupCode,
          emdType: a.emdType,
          rficCode: a.rficCode,
          ssrCode: a.ssrCode,
          owningCarrierCode: a.owningCarrierCode,
          currency: this.buildCurrencyStructure(currency),
        },
        redemptionType: REDEMPTION_TYPES[type] || REDEMPTION_TYPES.tier,
        legCode: currencyAncillary?.legCode || legCodeFirstSegment,
        quantity: 1,
      };
    });

    let travelInfo: any = {
      ...this.mapPassengerBasicInfo(passenger),
      ffNumber: passenger.passengerInfo.ffNumber,
      ffTierLevel: passenger.frequentFlyerName,
      eticket: passenger.ticketInfo?.eticket,
      ancillaries: mappedAncillaries,
    };
    if (type == 'cobrand') {
      travelInfo.cobrandCard = passenger.benefits.cobrandCode; //'AMEXPLT';
    }
    if (type == 'corporate') {
      travelInfo.corporateAccount = reservation?.clid || '';
    }
    const segment = firstSegment || flightSummary.currentSegment;
    return {
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'), //"2025-01-30T13:20:41.266747200"
      travelerInfo: [travelInfo],
      legs: [
        {
          legCode: legCodeFirstSegment,
          legRegion: flightSummary.legRegion,
          segments: [this.mapSegmentToApiFormat(segment)],
        },
      ],
    };
  }

  /**
   * Procesa respuesta genérica validando warnings y errores
   * @param response Respuesta de la API
   * @returns Respuesta procesada o lanza error si hay warnings/errores
   */
  static mapResponse(response: any) {
    if (!response) {
      throw new Error('Response is required');
    }

    const { warnings } = response;
    if (warnings?.length > 0) {
      throw new Error(warnings[0]);
    }

    const reason = response.travelerInfo?.[0]?.ancillaries?.[0]?.ancillary?.reason;
    if (reason) {
      throw new Error(reason);
    }

    return response;
  }

  static mapResponsePay(response) {
    const reason = response?.reason;
    if (reason) {
      throw new Error(reason);
    }
    return response;
  }

  static mapResponseReciptPay(response) {
    const reason = response?.reason;
    if (reason) {
      throw new Error(reason);
    }

    const pectabData = response?.pectabReceipt?.pectabData || null;
    const receiptData = response?.pectabReceipt?.receiptData || null;
    const transactionId = response?.receiptMetadata?.transactionId || null;
    const recordLocator = response?.receiptMetadata?.recordLocator || null;
    const totalAmount = response?.receiptMetadata?.totalAmount || null;

    return {
      success: response?.success ?? false,
      pectabData,
      receiptData,
      transactionId,
      recordLocator,
      totalAmount,
    };
  }

  /**
   * Genera request para asignar asientos
   * @param reservation Información de la reserva
   * @returns Objeto request para API de asientos
   */
  static setSeatRequest(
    reservation: Reservation,
    passenger: Passenger,
    seatSelected,
    seatOld,
    rollback
  ) {
    const { flightSummary, isStandBy, passengers, pnr } = reservation;
    const { currency, code } = seatSelected;
    const isCobrand = !!passenger?.benefits?.cobrandCode;
    const isTier = !!passenger?.benefits?.tier && !isCobrand;

    const isUpgrade = seatSelected?.isUpgrade === true;
    const isChange =
      (seatOld?.seatCode && seatOld?.seatCode?.length > 0 && code != seatOld?.code) || false;
    const isCorporate = false;

    const isBenefit = seatSelected?.status === SeatPassangerEstatus.BENEFIT;
    const validCost = !isBenefit && currency && currency?.total > 0;

    let seatStatus;
    if (isUpgrade) {
      seatStatus = SeatPassangerEstatus.ASSIGNED;
    } else if (seatSelected?.status === '') {
      seatStatus = '';
    } else if (validCost) {
      seatStatus = SeatPassangerEstatus.PENDING_PAYMENT;
    } else {
      seatStatus = SeatPassangerEstatus.ASSIGNED;
    }

    const isFreeUpgradeSeat =
      seatSelected?.currency?.total === 0 && seatStatus === SeatPassangerEstatus.ASSIGNED;

    const seatId = isUpgrade || isFreeUpgradeSeat ? '' : seatOld?.id || '';

    const seatCodeOld = seatOld?.seatCode || '';

    const requestSeat = {
      transactionDate: moment(),
      requestFrom: DEFAULT_CONSTANTS.REQUEST_FROM_FE,
      isStandBy: isStandBy,
      reservationCode: pnr,
      passengers: [
        {
          ...this.mapPassengerBasicInfo(passenger),
          ffNumber: passenger?.passengerInfo?.ffNumber || passenger?.frequentFlyerNumber,
          ffTierLevel: passenger?.passengerInfo?.ffTierLevel || '',
          cobrandCard: DEFAULT_CONSTANTS.COBRAND_CARD,
          corporateAccount: reservation?.clid || '',
          seats: [
            {
              /* ...
          seatSelected, */
              rollback: rollback,
              id: seatId,
              seatCode: seatSelected?.code || '',
              isChangeSeat: isChange,
              seatCodeOld: seatCodeOld,
              segmentCode: seatSelected?.segmentCode,
              isRedemptionCobrand: isCobrand,
              isRedemptionTier: isTier,
              isRedemptionCorporate: isCorporate,
              redemptionType: isCobrand
                ? 'COBRAND'
                : isTier
                ? 'TIER'
                : isCorporate
                ? 'CORPORATE'
                : '',
              emd: '',
              status: seatStatus,
              currency: {
                currencyCode: seatSelected?.currency?.currencyCode || 'MXN',
                base: isBenefit || isUpgrade ? 0 : seatSelected?.currency?.base || 0,
                taxes: isBenefit || isUpgrade ? 0 : seatSelected?.currency?.totalTax || 0,
                total: isBenefit || isUpgrade ? 0 : seatSelected?.currency?.total || 0,
              },
            },
          ],
          ticketInfo: {
            eticket: passenger?.ticketInfo?.eticket,
            coupons: this.mapPassengerCoupons(passenger),
          },
          eticket: passenger?.ticketInfo?.eticket,
        },
      ],
      legs: [
        {
          legCode: flightSummary?.legCode,
          legEntity: flightSummary?.legEntity,
          remainingTimeToCheckin: DEFAULT_CONSTANTS.REMAINING_TIME,
          segments: this.filterSegmentsWithSeats(
            flightSummary?.segments,
            passengers,
            seatSelected?.segmentCode
          ).map((segment, i) => ({
            ...this.mapSegmentToApiFormat(segment),
            remainingSegmentTimeToCheckin: DEFAULT_CONSTANTS.REMAINING_TIME,
            bookingCabin: DEFAULT_CONSTANTS.BOOKING_CABIN,
            segmentNumber: `${i + 1}`,
          })),
        },
      ],
      rollback: false,
    };

    return requestSeat;
  }

  static removeAllSeatsRequest(reservation: Reservation) {
    const { flightSummary, isStandBy, passengers, pnr } = reservation;

    const requestSeat = {
      transactionDate: moment(),
      requestFrom: 'FE_KIOSK',
      isStandBy: isStandBy,
      reservationCode: pnr,
      passengers: passengers
        .filter((passenger) => passenger?.checkinSelected || passenger?.isSelectedToCheckin)
        .map((passenger) => {
          const { seatsNew } = passenger;
          const isCobrand = passenger?.benefits?.cobrandCode ? true : false;
          const isTier = passenger?.benefits?.tier && !isCobrand ? true : false;

          return {
            nameNumber: passenger?.nameRefNumber,
            firstName: passenger?.firstName,
            lastName: passenger?.lastName,
            type: passenger?.paxType,
            ffNumber: passenger?.passengerInfo?.ffNumber || passenger?.frequentFlyerNumber,
            ffTierLevel: passenger?.passengerInfo?.ffTierLevel || '',
            cobrandCard: passenger?.benefits?.cobrandCode || '',
            corporateAccount: reservation?.clid || '',
            seats: seatsNew
              ?.filter(
                (seat) =>
                  seat?.status == SeatPassangerEstatus.PENDING_PAYMENT ||
                  seat?.currency?.total > 0 ||
                  seat?.status != null
              )
              .map((seatNew) => {
                const { seatCode, seatCodeOld, id, segmentCode, currency } = seatNew;
                const validCost = currency && currency?.total > 0;
                const isChange = seatCodeOld?.length > 0 && seatCode != seatCodeOld;

                return {
                  /* ...
                seatSelected, */
                  id: id || '',
                  seatCode: seatCode || '',
                  isChangeSeat: isChange,
                  seatCodeOld: seatCodeOld || '',
                  segmentCode: segmentCode,
                  isRedemptionCobrand: isCobrand,
                  isRedemptionTier: isTier,
                  redemptionType: isCobrand ? 'COBRAND' : isTier ? 'TIER' : '',
                  emd: '',
                  status: validCost
                    ? SeatPassangerEstatus.PENDING_PAYMENT
                    : SeatPassangerEstatus.ASSIGNED,
                  currency: {
                    currencyCode: currency?.currencyCode || 'MXN',
                    base: currency?.base || 0,
                    taxes: currency?.totalTax || 0,
                    total: currency?.total || 0,
                  },
                };
              }),
            ticketInfo: {
              eticket: passenger?.ticketInfo?.eticket,
              coupons: this.mapPassengerCoupons(passenger),
            },
            eticket: passenger?.ticketInfo?.eticket,
          };
        }),
      legs: [
        {
          legCode: flightSummary?.legCode,
          legEntity: flightSummary?.legEntity,
          remainingTimeToCheckin: flightSummary?.remainingTimeToCheckin,
          segments: flightSummary?.segments
            ?.filter((seg) =>
              passengers.find((p) => p.seats.find((s) => s.segmentCode === seg.segmentCode))
            )
            ?.map((segment, i) => ({
              segmentCode: segment?.segmentCode,
              remainingSegmentTimeToCheckin: segment?.remainingSegmentTimeToCheckin,
              aircraftType: segment?.aircraftType,
              origin: segment?.departureAirport,
              destination: segment?.arrivalAirport,
              marketingFlightCode: segment?.marketingFlightCode,
              marketingCarrier: segment?.marketingCarrier,
              operatingCarrier: segment?.operatingCarrier,
              operatingFlightCode: segment?.operatingFlightCode,
              departureDate: segment?.departureDateTime,
              arrivalDate: segment?.arrivalDateTime,
              fareFamily: segment?.fareFamily,
              bookingClass: segment?.bookingClass,
              cabinClass: segment?.cabinClass,
              bookingCabin: segment?.cabinClass,
              departureAirportTimeZoneId: segment?.departureAirportTimeZoneId,
              segmentNumber: `${i + 1}`, // Pendiente
              segmentEntity: segment?.segmentEntity,
            })),
        },
      ],
      rollback: true,
    };

    return requestSeat;
  }

  static respSeat(res): ResponseSeatInt[] {
    const { warnings } = res;

    if (warnings && warnings.length > 0) {
      throw new Error(warnings[0]);
    }

    if (!res || !res.length) {
      throw new Error('La respuesta del servidor es vacía');
    }

    return res.map((item) => ({
      nameNumber: item.nameNumber,
      seats: item.seats.map((seat) => ({
        id: seat.airExtraItemNumber,
        airExtraItemNumber: seat.airExtraItemNumber,
        emdType: seat.emdType,
        code: seat.code,
        endType: seat.endType,
        group: seat.group,
        isChangeSeat: seat.isChangeSeat,
        isChangesSeat: seat.isChangesSeat,
        name: seat.name,
        owningCarrierCode: seat.owningCarrierCode,
        redemptionType: seat.redemptionType,
        rfic: seat.rfic,
        seatCode: seat.seatCode,
        seatCodeOld: seat.seatCodeOld,
        segmentCode: seat.segmentCode,
        segmentIndicator: seat.segmentIndicator,
        ssrCode: seat.ssrCode,
        vendor: seat.vendor,
      })),
    }));
  }

  /**
   *
   * @param resp Respuesta ya mapeada por `respSeat`.
   * @param seatSelected Asiento que el pasajero solicitó (code/segmentCode).
   */
  static validateSeatAssigned(resp: ResponseSeatInt[], seatSelected): void {
    const seats = (resp ?? []).flatMap((p) => p.seats || []);
    const assigned =
      seats.find((s) => s.segmentCode === seatSelected?.segmentCode) || seats[0];

    if (!assigned || assigned.seatCode !== seatSelected?.code) {
      throw new Error(
        `Asiento ${seatSelected?.code} no asignado (rollback a ${assigned?.seatCode})`
      );
    }
  }

  /**
   * Procesa respuesta de eliminación de servicios adicionales
   * @param response Respuesta de la API
   * @returns Respuesta procesada o lanza error si hay warnings/errores
   */
  static mapRemoveAncillariesResponse(response: any) {
    const { warnings = [], travelerInfo = [] } = response;
    if (warnings.length > 0) {
      throw new Error(warnings[0]);
    }
    // Recorremos cada viajero y sus ancillaries para detectar errores
    for (const traveler of travelerInfo) {
      if (traveler.ancillaries?.length > 0) {
        const hasError = traveler.ancillaries.find(
          (ancillary) => ancillary?.ancillary?.reason && ancillary.ancillary.reason !== ''
        );
        if (hasError) {
          throw new Error(hasError.ancillary.reason);
        }
      }
    }

    return response;
  }

  /**
   * Construye el body para el endpoint POST /tc/benefits/benefitBySegment.
   * Extrae los datos de negocio de la reservación y los estructura en el formato requerido.
   *
   * @param reservation Información de la reserva
   * @returns Objeto request con la estructura benefitBySegment
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static buildBenefitBySegmentBody(reservation: Reservation): BenefitBySegmentRequest {
    const { flightSummary, passengers, segments, isEmployee, isStandBy, isNewBF } = reservation;
    const { legCode, legRegion } = flightSummary;

    const fareFamily =
      flightSummary.currentSegment?.fareFamily ||
      flightSummary.fareFamily ||
      segments[0]?.fareFamily ||
      '';

    const aeroSegment =
      segments.find((segment) => segment.operatingCarrier === 'AM') ?? segments[0];
    const segmentCode = aeroSegment.segmentCode;

    const ticketAdt =
      passengers.find((p) => p.paxType === PAX_TYPE.ADULT)?.ticketInfo?.eticket || '';
    const ticketInf =
      passengers.find((p) => p.paxType === PAX_TYPE.INFANT)?.ticketInfo?.eticket || '';

    return {
      benefitBySegment: [
        {
          legCode,
          segments: [
            {
              segmentCode,
              fareFamily,
              isEmployee,
              isStandBy,
              legRegion,
              ticketAdt,
              ticketInf,
              isNewBF,
            },
          ],
        },
      ],
    };
  }

  /**
   * Mapea la respuesta de la api de obtener el costo de los
   * servicios de equipaje y otros servicios adicionales.
   *
   * @param response respuesta de la api
   * @param reservation informacion de la reserva
   * @returns el costo mapeado de los servicios de equipaje y otros servicios adicionales.
   */
  static mapAncillariesFare(response: BaggageAndServicesInt, reservation: Reservation) {
    // CI-21988: ambos destructurings lanzaban si el argumento venía undefined,
    // y `passengers.map()` era el origen del error "reading 'map'".
    const { carryOn, checkedBags, seatSelection, infantCheckedBaggage } =
      response ?? ({} as BaggageAndServicesInt);
    const passengers = reservation?.passengers ?? [];

    const baggageIncludes = passengers.map((passenger) => {
      const { nameRefNumber, hasInfant, paxType } = passenger;

      const offerCarryOn = this.createCarryOnBags(carryOn);
      const offerCheckBags = this.createCheckedBags(
        checkedBags,
        paxType,
        hasInfant,
        infantCheckedBaggage
      );

      return { nameRefNumber, offerCheckBags, offerCarryOn };
    });

    return {
      baggageIncludes,
      seatSelection,
    };
  }

  static requestBagtag(reservation: Reservation, passenger: Passenger, bag: BagsPassangerInt) {
    const { pnr: reservationCode, codeShare, flightSummary } = reservation;
    const codeBagge = (bag?.rficSubcode || bag?.rficCode) ?? '08K';

    return {
      reservationCode,
      bookedLegCollection: [
        {
          codeShare,
          legCode: flightSummary.legCode,
          segments: flightSummary.segments
            .filter((segment) => segment.windowSegmentStatus === 'CKI')
            .map((segment) => ({
              bookingClass: segment.bookingClass,
              departureAirport: segment.departureAirport,
              departureDateTime: segment.departureDateTime,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              segmentCode: segment.segmentCode,
              baggageDropAllowed: true,
              arrivalAirport: segment.arrivalAirport,
            })),
        },
      ],
      passengers: [
        {
          shortBaggageDrop: passenger.shortBaggageDrop,
          lastName: passenger.lastName,
          passengerIds:
            passenger.passengerIds?.map((passengerId) => ({
              id: passengerId.id,
              segmentCode: passengerId.segmentCode,
            })) || [],
          baggageInfo: {
            codeBagge,
            unit: 'KG',
            weight: bag?.weightBag?.toString()?.padStart(2, '0') || '02',
          },
          passengerNameNumber: passenger.nameRefNumber,
          beneficiaryTier: passenger?.frequentFlyerName || '',
          type: this.getPaxTypeShort(passenger.paxType),
        },
      ],
    };
  }

  /**
   * Convierte el tipo de pasajero al formato corto requerido
   * @param paxType Tipo de pasajero largo
   * @returns Tipo de pasajero en formato corto
   */
  private static getPaxTypeShort(paxType: string): string {
    const paxTypeMapping = {
      ADULT: 'ADT',
      CHILD: 'CHD',
      INFANT: 'INF',
    };
    return paxTypeMapping[paxType] || 'ADT';
  }

  private static totalsByProduct(array) {
    const total = array.reduce((acc, passenger) => {
      passenger?.products.forEach((product) => {
        acc += product.total.total;
      });
      return acc;
    }, 0);

    return Math.round(total * 100) / 100;
  }

  //#region Payments Compose (/pc/emit-ticket-ancillary) — portado de am-ssbd-kiosks (CI-21667 / CI-22484 / CI-23340)

  //Normaliza cualquier importe del carrito a número
  private static toPaymentAmount(value: unknown): number {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  }

  /**
   * Normaliza la cantidad del producto a un entero >= 1: el backend rechaza la emisión
   * con `ERR.PC.EMIT_TICKET_ANCILLARY.BAD_REQUEST` si `FeeDetails` es 0 (CI-23340).
   */
  private static toPaymentQuantity(value: unknown): number {
    const quantity = Math.trunc(Number(value));
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /**
   * Construye el legCode del primer segmento (ORIGEN_DESTINO_YYYY-MM-DD).
   * CI-18515: el backend solo acepta el primer tramo.
   */
  private static buildFirstSegmentLegCode(flightSummary: FlightSummary): string {
    const firstSegment = flightSummary?.segments?.[0];

    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      const { departureAirport, arrivalAirport } = firstSegment;

      if (departureAirport && arrivalAirport && departureDate) {
        return `${departureAirport}_${arrivalAirport}_${departureDate}`;
      }
    }

    return flightSummary?.legCode || '';
  }

  private static resolveProductType(groupCode: string): string {
    return PAYMENT_PRODUCT_TYPE_BY_GROUP[groupCode?.toUpperCase()] || 'ITINERARY_PART';
  }

  /**
   * Normaliza el emdType al valor esperado por el backend.
   * Las maletas llegan con el código numérico ('2') y los asientos con el texto ('Associated').
   */
  private static mapPaymentEmdType(emdType: unknown): string {
    const value = `${emdType ?? ''}`;
    return value === '2' || value.toLowerCase() === 'associated' ? 'Associated' : 'Standalone';
  }

  /**
   * Detalle de impuestos dentro de currency.
   * Acepta el arreglo de ancillaries ([{ price, description }]) y el mapa de asientos
   * ({ MXA: 221 }); si no hay detalle arma un impuesto único con el total de impuestos.
   * Los productos redimidos con puntos/millas mandan los impuestos en 0.
   */
  private static mapPaymentTaxDetails(
    currency: PaymentSourceCurrencyInt,
    fallbackTaxDetails: unknown,
    isRedemption: boolean
  ): PaymentTaxDetailInt[] {
    const taxDetails = currency?.taxDetails ?? fallbackTaxDetails;
    const price = (value: unknown) => (isRedemption ? 0 : this.toPaymentAmount(value));

    if (Array.isArray(taxDetails) && taxDetails.length > 0) {
      // CVT20-14569: soportar { price } y { amount } (response Chubb).
      return taxDetails.map((tax: { price?: unknown; amount?: unknown; description?: string }) => ({
        price: price(tax?.price ?? tax?.amount),
        description: tax?.description || DEFAULT_CONSTANTS.TAX_CODE,
        exempt: false,
      }));
    }

    if (taxDetails && typeof taxDetails === 'object' && !Array.isArray(taxDetails)) {
      const mapTaxDetails = Object.entries(taxDetails).map(([description, value]) => ({
        price: price(value),
        description,
        exempt: false,
      }));

      if (mapTaxDetails.length > 0) {
        return mapTaxDetails;
      }
    }

    return [
      {
        price: price(currency?.totalTax ?? currency?.taxes),
        description: DEFAULT_CONSTANTS.TAX_CODE,
        exempt: false,
      },
    ];
  }

  //Nodo currency del producto
  private static mapPaymentCurrency(
    product: PaymentSourceProductInt,
    currencyCode: string
  ): PaymentCurrencyInt {
    const currency = product?.currency;
    const total = this.toPaymentAmount(currency?.total);

    return {
      currencyCode: currency?.currencyCode || currencyCode,
      base: this.toPaymentAmount(currency?.base),
      taxes: this.toPaymentAmount(currency?.totalTax ?? currency?.taxes),
      total,
      totalPaymentForPoints: 0,
      totalPaymentForCard: total,
      taxDetails: this.mapPaymentTaxDetails(
        currency,
        product?.currencyPerLeg?.[0]?.currency?.taxDetails,
        this.isRedemptionProduct(product)
      ),
    };
  }

  //Unifica maletas, carry-on, asientos, seguro Chubb y upgrade en el producto de emisión
  private static mapPaymentProduct(
    product: PaymentSourceProductInt,
    legCode: string,
    currencyCode: string,
    fallbackCode = '',
    type?: string
  ): PaymentProductInt {
    const groupCode = product?.groupCode || product?.group || '';

    return {
      purchaseType: 'REVENUE',
      name: product?.commercialName || product?.nameDescription || product?.name || '',
      code: product?.rficSubcode || product?.code || fallbackCode,
      owningCarrierCode: product?.owningCarrierCode || OperatingCarrier.AM,
      emdType: this.mapPaymentEmdType(product?.emdType),
      groupCode,
      id: `${product?.airExtraItemNumber || product?.id || ''}`,
      segmentIndicator: product?.segmentIndicator || '',
      rfic: product?.rficCode || product?.rfic || '',
      ssrCode: product?.ssrCode || 'ASVC',
      vendor: product?.vendor || 'MMGR',
      quantity: this.toPaymentQuantity(product?.quantity),
      legCode: product?.legCode || legCode,
      currency: this.mapPaymentCurrency(product, currencyCode),
      type: type || this.resolveProductType(groupCode),
    };
  }

  //Productos por cobrar del pasajero (maletas, carry-on, asientos y seguro Chubb)
  private static getPaymentProducts(
    passenger: Passenger,
    legCode: string,
    currencyCode: string
  ): PaymentProductInt[] {
    const isPayableBag = (bag: { status?: unknown; isExtraWeight?: boolean; selected?: boolean }) =>
      (bag?.status === StatusBag.TO_PAY || bag?.isExtraWeight) && bag?.selected;

    const bags = passenger?.bagStack?.filter(isPayableBag) || [];
    const carryOnBags = passenger?.bagStackCarryon?.filter(isPayableBag) || [];
    const carryOnOfferBags = passenger?.bagOfferCarryOnStack?.filter(isPayableBag) || [];
    const seats =
      passenger?.seatsNew?.filter(
        (seat) => seat?.status === SeatPassangerEstatus.PENDING_PAYMENT
      ) || [];

    // CVT20-14569: seguros Chubb del flujo estandar (bagInsuranceProduct) y de
    // manage-extras (chubbInsuranceProducts, multiples productos).
    // CVT20-14664: un seguro ya emitido (ancillary PAID con EMD) no se reenvia; si no,
    // Sabre responde "AE with id XX is not open for use" en un segundo pago.
    const isAlreadyEmitted = (rficSubcode: string): boolean =>
      hasPaidChubbAncillary(passenger?.ancillaries, rficSubcode);

    const insurance: PaymentSourceProductInt[] = [];
    if (
      passenger?.hasBagInsurance &&
      passenger?.bagInsuranceSource === 'kiosk' &&
      passenger?.bagInsuranceProduct &&
      !isAlreadyEmitted(passenger.bagInsuranceProduct.rficSubcode)
    ) {
      insurance.push(passenger.bagInsuranceProduct);
    }
    (passenger?.chubbInsuranceProducts || []).forEach((product) => {
      const alreadyIncluded = insurance.some((src) => src.rficSubcode === product.rficSubcode);
      if (!alreadyIncluded && !isAlreadyEmitted(product.rficSubcode)) {
        insurance.push(product);
      }
    });

    return [
      ...[...bags, ...carryOnBags, ...carryOnOfferBags].map((product) =>
        this.mapPaymentProduct(product, legCode, currencyCode, '', 'ITINERARY_PART')
      ),
      // Asiento: EMD Associated de tipo SEGMENT; el backend arma el AssociatedFlight
      // con el segmentCode (sin él: "AssociatedFlight missing for EMD Type - Associated").
      ...seats.map((seat) => ({
        ...this.mapPaymentProduct(seat, legCode, currencyCode, SEAT_ANCILLARY_CODE),
        segmentCode: seat?.segmentCode,
      })),
      ...insurance.map((product) => this.mapPaymentProduct(product, legCode, currencyCode)),
    ];
  }

  //Pasajero del payload de emisión
  private static buildPaymentPassenger(
    passenger: Passenger,
    products: PaymentProductInt[]
  ): PaymentPassengerInt {
    return {
      firstName: passenger?.firstName,
      lastName: passenger?.lastName,
      type: this.getPaxTypeShort(passenger?.paxType),
      nameRefNumber: passenger?.nameRefNumber,
      ticketInfo: {
        eticket: passenger?.ticketInfo?.eticket,
        coupons: this.mapPassengerCoupons(passenger),
      },
      products,
    };
  }

  /**
   * Itinerario del payload de emisión.
   * Se envían todos los segmentos del leg en check-in; el couponNumber se cruza
   * por segmentCode contra los cupones del boleto.
   */
  private static getPaymentFlightDetails(
    flightSummary: FlightSummary,
    passengers: Passenger[],
    legCode: string
  ): PaymentFlightDetailInt[] {
    const coupons = passengers.flatMap((passenger) => passenger?.ticketInfo?.coupons || []);

    return [
      {
        legCode,
        windowLegStatus: flightSummary?.windowLegStatus,
        segments:
          flightSummary?.segments?.map((segment, index) => {
            const couponNumber = coupons.find(
              (coupon) => coupon?.segmentCode === segment?.segmentCode
            )?.couponNumber;

            return {
              segmentCode: segment?.segmentCode,
              segmentNumber: String(segment?.sequence ?? index + 1),
              marketingCarrier: segment?.marketingCarrier,
              marketingFlightCode: segment?.marketingFlightCode,
              operatingCarrier: segment?.operatingCarrier,
              operatingFlightCode: segment?.operatingFlightCode,
              bookingClass: segment?.bookingClass,
              departureAirport: segment?.departureAirport,
              departureDateTime: segment?.departureDateTime,
              arrivalAirport: segment?.arrivalAirport,
              arrivalDateTime: segment?.arrivalDateTime,
              departureCountry: segment?.departureCountry,
              cabinClass: segment?.cabinClass,
              fareFamily: segment?.fareFamily === 'BASIC' ? 'MAIN_BASIC' : segment?.fareFamily,
              remainingSegmentTimeToCheckin: segment?.remainingSegmentTimeToCheckin,
              departureAirportTimeZoneId: segment?.departureAirportTimeZoneId,
              couponNumber: String(couponNumber ?? index + 1),
            };
          }) || [],
      },
    ];
  }

  //Total a cobrar: suma de currency.total de los productos, redondeado a 2 decimales
  private static totalPaymentProducts(passengers: PaymentPassengerInt[]): number {
    const total = passengers.reduce(
      (acc, passenger) =>
        acc +
        (passenger?.products || []).reduce(
          (subtotal, product) => subtotal + this.toPaymentAmount(product?.currency?.total),
          0
        ),
      0
    );

    return Math.round(total * 100) / 100;
  }

  //Arma el request final (pasajeros ya mapeados) con itinerario y datos de la tarjeta
  private static buildEmitTicketRequest(
    reservation: Reservation,
    passengers: PaymentPassengerInt[],
    legCode: string,
    pcc: PaymentPccInt,
    payData: PaymentInfoInt,
    total: number
  ): EmitTicketAncillaryRequestInt {
    // MMYY de la fecha en curso (el PinPad no expone la vigencia real)
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const expireDate = month + year;

    return {
      reservationCode: reservation?.pnr,
      total,
      isPinPad: true,
      passengers,
      flightDetails: this.getPaymentFlightDetails(
        reservation?.flightSummary,
        reservation?.passengers || [],
        legCode
      ),
      paymentDetails: [
        {
          cardCode: payData?.cartInfo?.cardCode ?? '',
          cardNumber: payData?.cartInfo?.ccNumber ?? '',
          cardSecurityCode: '0000',
          expireDate,
          amount: total,
          transactionID: payData?.autInfo?.transactionId || payData?.autInfo?.operationCode || '',
          approvalCode: payData?.autInfo?.approvalCode ?? '',
          idPaymentDetail: 1,
        },
      ],
      pcc,
    };
  }

  /**
   * Request de emisión de ancillaries con pago (maletas, carry-on, asientos, seguro Chubb).
   * POST /pc/emit-ticket-ancillary
   *
   * @param reservation Información de la reserva
   * @param currency Código de moneda de respaldo cuando el producto no lo trae
   * @param pcc Punto de venta (cityCode, stationNumber, accountCode)
   * @param payData Datos de tarjeta y autorización del PinPad
   */
  static emitTicketAncillaryRequest(
    reservation: Reservation,
    currency: string,
    pcc: PaymentPccInt,
    payData: PaymentInfoInt
  ): EmitTicketAncillaryRequestInt {
    const currencyCode = currency || DEFAULT_CONSTANTS.CURRENCY_CODE;
    const legCode = this.buildFirstSegmentLegCode(reservation?.flightSummary);
    const passengers = (reservation?.passengers || [])
      .map((passenger) => ({
        passenger,
        products: this.getPaymentProducts(passenger, legCode, currencyCode),
      }))
      .filter(({ products }) => products.length > 0)
      .map(({ passenger, products }) => this.buildPaymentPassenger(passenger, products));

    return this.buildEmitTicketRequest(
      reservation,
      passengers,
      legCode,
      pcc,
      payData,
      this.totalPaymentProducts(passengers)
    );
  }

  /**
   * Request de emisión del upgrade con pago.
   * POST /pc/emit-ticket-ancillary
   *
   * @param upgradeProducts Productos de upgrade (oferta + airExtraItemNumber) por pasajero
   * @param totalAmount Total cobrado en el PinPad; si no viene se suma desde los productos
   */
  static emitTicketUpgradeRequest(
    reservation: Reservation,
    upgradeProducts: { passenger: Passenger; products: PaymentSourceProductInt[] }[],
    currency: string,
    pcc: PaymentPccInt,
    payData: PaymentInfoInt,
    totalAmount?: number
  ): EmitTicketAncillaryRequestInt {
    const currencyCode = currency || DEFAULT_CONSTANTS.CURRENCY_CODE;
    const legCode = this.buildFirstSegmentLegCode(reservation?.flightSummary);
    const passengers = upgradeProducts
      .filter(({ products }) => products?.length > 0)
      .map(({ passenger, products }) =>
        this.buildPaymentPassenger(
          passenger,
          products.map((product) => this.mapPaymentProduct(product, legCode, currencyCode))
        )
      );

    const total =
      this.toPaymentAmount(totalAmount) > 0
        ? Math.round(this.toPaymentAmount(totalAmount) * 100) / 100
        : this.totalPaymentProducts(passengers);

    return this.buildEmitTicketRequest(reservation, passengers, legCode, pcc, payData, total);
  }

  //#endregion Payments Compose

  private static getPassengerData(passengers: Passenger[], flightSummary: FlightSummary) {
    return passengers
      .map((p) => {
        const { firstName, lastName, nameRefNumber, ticketInfo } = p;
        const products = this.getProductsPerPassenger(p, flightSummary, ticketInfo?.eticket);
        if (products.length > 0) {
          return {
            firstName,
            lastName,
            nameRefNumber,
            ticketNumber: ticketInfo?.eticket,
            products,
          };
        }
        return null;
      })
      .filter((p) => p !== null);
  }

  //Normaliza el taxDetails de una moneda al shape requerido por el contrato de emisión.
  //Sabre lo devuelve en dos formas: arreglo [{ price, description }] para maletas,
  //y mapa de código de impuesto a monto ({ MX: 64 }) para asientos.
  private static mapTaxDetails(
    taxDetails: any,
    isRedemption: boolean
  ): { price: number; description: string }[] {
    if (Array.isArray(taxDetails)) {
      // CVT20-14569: soportar { price } y { amount } (response Chubb).
      return taxDetails.map((entry: { price?: number; amount?: number; description?: string }) => ({
        price: isRedemption ? 0 : entry?.price ?? entry?.amount ?? 0,
        description: entry?.description ?? '',
      }));
    }

    //Forma de mapa: la llave es el código del impuesto y el valor su monto
    if (taxDetails && typeof taxDetails === 'object' && !Array.isArray(taxDetails)) {
      return Object.entries(taxDetails).map(([description, price]) => ({
        price: isRedemption ? 0 : Number(price) || 0,
        description,
      }));
    }

    return [];
  }

  //Determina si un producto fue pagado con puntos/millas (Redemption_Payment).
  //No se usa redemptionType: backend lo regresa con valores de redencion ('TIER') incluso en
  //asientos pagados, por lo que solo las banderas isRedemption* son señal confiable.
  private static isRedemptionProduct(el: any): boolean {
    return !!(
      el?.isRedemptionTier ||
      el?.isRedemptionCobrand ||
      el?.isRedemptionFF ||
      el?.isRedemptionCorporate
    );
  }

  private static getProductsPerPassenger(
    passenger: Passenger,
    flightSummary: FlightSummary,
    ticket: string
  ) {
    const mapBagToProduct = (el) => {
      const associatedFlights =
        flightSummary?.segments
          ?.filter((seg) => seg.operatingCarrier === 'AM')
          ?.map((seg) => {
            const departureDate = seg.departureDateTime
              ? seg.departureDateTime.substring(0, 10)
              : null;
            const couponNumber = passenger?.ticketInfo?.coupons?.find(
              (cup) => cup.segmentCode === seg.segmentCode
            )?.couponNumber;

            return {
              segmentID: seg.id,
              segmentNumber: seg.sequence,
              carrierCode: seg.operatingCarrier,
              operatingCarrierCode: seg.operatingCarrier,
              flightNumber: seg.marketingFlightCode,
              classOfService: seg.bookingClass,
              departureAirport: seg.departureAirport,
              departureDate,
              arrivalAirport: seg.arrivalAirport,
              ticketNumber: ticket,
              couponNumber,
            };
          }) || [];

      return {
        code: el.rficSubcode,
        name: el.commercialName || el.nameDescription,
        group: el.groupCode,
        segmentIndicator: el.segmentIndicator,
        rfic: el.rficCode,
        emdType: el.emdType == '2' ? 'Associated' : 'Standalone',
        airExtraItemNumber: el.airExtraItemNumber || el.id,
        owningCarrierCode: el.owningCarrierCode ?? 'AM',
        vendor: el.vendor,
        quantity: 1,
        total: {
          base: el.currency?.base ?? 0,
          taxes: el.currency?.totalTax ?? 0,
          total: el.currency?.total ?? 0,
          taxDetails: this.mapTaxDetails(
            el.currency?.taxDetails ?? el.currencyPerLeg?.[0]?.currency?.taxDetails,
            this.isRedemptionProduct(el)
          ),
        },
        associatedFlights,
      };
    };

    const bags =
      passenger?.bagStack
        ?.filter((b) => (b.status === StatusBag.TO_PAY || b.isExtraWeight) && b.selected)
        .map(mapBagToProduct) || [];

    const carryOnBags =
      passenger?.bagStackCarryon
        ?.filter((b) => (b.status === StatusBag.TO_PAY || b.isExtraWeight) && b.selected)
        .map(mapBagToProduct) || [];

    const carryOnOfferBags =
      passenger?.bagOfferCarryOnStack
        ?.filter((b) => (b.status === StatusBag.TO_PAY || b.isExtraWeight) && b.selected)
        .map(mapBagToProduct) || [];

    const seats =
      passenger?.seatsNew
        ?.filter((s) => s.status === SeatPassangerEstatus.PENDING_PAYMENT)
        .map((el) => {
          const associatedFlights =
            flightSummary?.segments
              ?.filter((seg) => seg.operatingCarrier === 'AM' && seg.segmentCode === el.segmentCode)
              ?.map((seg) => {
                const departureDate = seg.departureDateTime
                  ? seg.departureDateTime.substring(0, 10)
                  : null;
                const couponNumber = passenger?.ticketInfo?.coupons?.find(
                  (cup) => cup.segmentCode === seg.segmentCode
                )?.couponNumber;

                return {
                  segmentID: seg.id,
                  segmentNumber: seg.sequence,
                  carrierCode: seg.operatingCarrier,
                  operatingCarrierCode: seg.operatingCarrier,
                  flightNumber: seg.marketingFlightCode,
                  classOfService: seg.bookingClass,
                  departureAirport: seg.departureAirport,
                  departureDate,
                  arrivalAirport: seg.arrivalAirport,
                  ticketNumber: ticket,
                  couponNumber,
                };
              }) || [];

          return {
            code: SEAT_ANCILLARY_CODE,
            name: el.name,
            group: el.group,
            segmentIndicator: el.segmentIndicator,
            rfic: el.rfic,
            emdType: el.emdType,
            airExtraItemNumber: el.airExtraItemNumber,
            owningCarrierCode: el.owningCarrierCode,
            vendor: el.vendor,
            quantity: 1,
            total: {
              base: el.currency.base,
              taxes: el.currency.totalTax ?? 0,
              total: el.currency.total ?? 0,
              taxDetails: this.mapTaxDetails(el.currency?.taxDetails, this.isRedemptionProduct(el)),
            },
            associatedFlights,
          };
        }) || [];

    // CVT20-14569: seguros Chubb del flujo estandar (bagInsuranceProduct) y de
    // manage-extras (chubbInsuranceProducts, multiples productos).
    //
    // CVT20-14664: un seguro YA EMITIDO (ancillary PAID con EMD en este pasajero)
    // NO debe reenviarse al emit-ticket. A diferencia de bags/seats —que filtran
    // por estado pendiente (TO_PAY / PENDING_PAYMENT)— el seguro no tenia filtro y
    // arrastraba productos de un pago anterior, provocando en un segundo pago el
    // error de Sabre "AE with id XX is not open for use"
    // (ERR.PB.EMIT.TICKET.SABRE.ERROR). El EMD distingue un cobro real (cerrado) de
    // un ONHOLD reservado por "Agregar", que si debe emitirse.
    const isAlreadyEmitted = (rficSubcode: string): boolean =>
      hasPaidChubbAncillary(passenger?.ancillaries, rficSubcode);

    const insuranceSources = [];
    if (
      passenger?.hasBagInsurance &&
      passenger?.bagInsuranceSource === 'kiosk' &&
      passenger?.bagInsuranceProduct &&
      !isAlreadyEmitted(passenger.bagInsuranceProduct.rficSubcode)
    ) {
      insuranceSources.push(passenger.bagInsuranceProduct);
    }
    if (Array.isArray(passenger?.chubbInsuranceProducts)) {
      passenger.chubbInsuranceProducts.forEach((prod) => {
        const alreadyIncluded = insuranceSources.some(
          (src) => src.rficSubcode === prod.rficSubcode
        );
        if (!alreadyIncluded && !isAlreadyEmitted(prod.rficSubcode)) {
          insuranceSources.push(prod);
        }
      });
    }

    const insurance = insuranceSources.map((prod) => ({
      code: prod.rficSubcode,
      name: prod.commercialName,
      group: prod.groupCode,
      segmentIndicator: prod.segmentIndicator,
      rfic: prod.rficCode,
      emdType: prod.emdType === '2' ? 'Associated' : 'Standalone',
      airExtraItemNumber: prod.airExtraItemNumber || '',
      owningCarrierCode: prod.owningCarrierCode,
      vendor: prod.vendor,
      quantity: 1,
      total: {
        base: prod.currency.base,
        taxes: prod.currency.totalTax,
        total: prod.currency.total,
        taxDetails: this.mapTaxDetails(prod.currency.taxDetails, false),
      },
    }));

    return [...bags, ...carryOnBags, ...carryOnOfferBags, ...seats, ...insurance];
  }

  /**
   * Genera request para verificar elegibilidad de BagTag
   * @param reservation Información de la reserva
   * @param passengers Lista de pasajeros seleccionados
   * @returns Objeto request para API de elegibilidad BagTag
   */
  static requestElegibilityBagtag(reservation: Reservation, passengers: Passenger[]) {
    const { flightSummary, pnr, isStandBy, codeShare } = reservation;
    return {
      reservationCode: pnr,
      bookedLegCollection: [
        {
          codeShare: codeShare || false,
          legCode: flightSummary.legCode,
          segments: flightSummary.segments
            .filter((segment) => segment.windowSegmentStatus === 'CKI')
            .map((segment) => ({
              bookingClass: segment.bookingClass,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              farebasis: segment.farebasis,
              segmentCode: segment.segmentCode,
              windowSegmentStatus: segment.windowSegmentStatus,
              fareFamily: segment.fareFamily,
              departureAirport: segment.departureAirport,
              departureCountry: segment.departureCountry,
              departureDateTime: segment.departureDateTime,
              departureAirportTimeZoneId: segment.departureAirportTimeZoneId,
              arrivalAirport: segment.arrivalAirport,
              arrivalCountry: segment.arrivalCountry,
              arrivalDateTime: segment.arrivalDateTime,
              arrivalAirportTimeZoneId: segment.arrivalAirportTimeZoneId,
              segmentStatusCode: segment.segmentStatusCode,
            })),
        },
      ],
      isStandBy: isStandBy,
      passengers: passengers.map((passenger) => ({
        lastName: passenger.lastName,
        passengerNameNumber: passenger.nameRefNumber,
        type: this.getPaxTypeShort(passenger.paxType),
        ancillaries:
          passenger.bagStack
            .filter((b) => b.selected && !b.used && !!b.rficSubcode)
            ?.map((ancillary) => ({
              status: ancillary.status,
              code: ancillary.rficSubcode,
              nameDescription: ancillary.nameDescription || ancillary.commercialName,
              segmentCode: flightSummary.segments
                .filter((seg) => seg.operatingCarrier == 'AM')
                .map((seg) => seg.segmentCode)
                .join('.'),
              isRedemptionCobrand: ancillary.isRedemptionCobrand || false,
              isRedemptionTier: ancillary.isRedemptionTier || false,
              isRedemptionFF: ancillary.isRedemptionFF || false,
              isRedemptionCorporate: ancillary.isRedemptionCorporate || false,
            })) || [],
      })),
    };
  }

  // El archivo agrupa sus métodos por región (privados primero, públicos después),
  // convención opuesta a member-ordering; se respeta el orden existente del archivo.
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static paymentReciptRequest(
    reservation: Reservation,
    payData: PaymentInfoInt,
    language,
    upgradeItems: UpgradeReceiptItem[] = [],
    insuranceTotal = 0
  ) {
    const { passengers: passengersReservation, pnr, flightSummary } = reservation;
    const passengers = this.getPassengerData(passengersReservation, flightSummary);
    // `issuedEmds` se envia como el objeto { weightUnit, emdItems } que retorna
    // buildIssuedEmds (shape que el backend del recibo espera). La DESCRIPCION del
    // ticket viaja dentro de emdItems.
    const emdItems = this.buildIssuedEmds(passengersReservation, upgradeItems);

    // CI-23205: `totalsByProduct` solo suma `passenger.products` (asientos, maletas),
    // que NO incluye el upgrade. Se agrega el total de los upgrades para que el
    // IMPORTE global del recibo también refleje el monto del ascenso.
    const upgradeTotal = (upgradeItems || []).reduce((sum, item) => sum + (item?.total || 0), 0);
    // CVT20-14858: el seguro se cobra pero NO llega a `totalsByProduct` al generar el recibo,
    // porque `clearEmittedInsuranceAfterTicket` (CVT20-14665) vacia los productos de seguro
    // del pasajero justo tras emitir, ANTES del recibo. Por eso el IMPORTE salia en 0.00. El
    // total se calcula en payment-success ANTES de limpiar y se inyecta aqui (igual que el
    // upgrade), solo para el IMPORTE: no se agrega linea de descripcion.
    const paymentTotal =
      Math.round((this.totalsByProduct(passengers) + upgradeTotal + (insuranceTotal || 0)) * 100) /
      100;

    return {
      purchaseOrder: {
        recordLocator: pnr,
        paymentInfo: {
          cardNumber: payData.cartInfo?.ccNumber,
          cardHolderName: payData.cartInfo?.cardHolderName ?? '',
          cardType: payData.cartInfo?.cardType ?? '',
          expirationDate: payData.cartInfo?.expiryDate ?? '',
          pinPadNotPresent: true,
          digitalSignatureProvided: false,
          aprname: payData.cartInfo?.cardType ?? '',
          alabel: `${payData.autInfo.cbrand}  ${payData.autInfo.cinstr}`,
          arqc: payData.autInfo.carqc,
          aid: payData.autInfo.caid,
          defaultExpirationDateUsed: false,
          cardTokens: [],
        },
        paymentAmount: {
          total: paymentTotal,
          currencyCode: 'MXN',
        },
      },
      authorizationResult: {
        approvalCode: payData.autInfo?.approvalCode ?? '',
        merchantAccountNumber: payData.autInfo?.merchantAccountNumber ?? '',
      },
      issuedEmds: emdItems,
      language: language,
    };
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  static buildIssuedEmds(passengers, upgradeItems: UpgradeReceiptItem[] = []) {
    const emdItems = [];
    let weightUnit = '';

    // --- UPGRADE (CI-23205) ---
    // Los datos (nombre del producto Premier + precio) se calculan al momento del
    // pago y se preservan en GlobalsService, porque el flujo post-pago limpia las
    // ofertas del upgrade antes de generar el recibo.
    (upgradeItems || []).forEach((item) => {
      if (item && item.total > 0) {
        emdItems.push({
          type: 'EMD_UPGRADE',
          quantity: item.quantity || 1,
          total: item.total,
          description: item.description,
        });
      }
    });

    // --- ASIENTOS ---
    const allSeats = passengers.flatMap((p) =>
      (p.seatsNew || []).filter((seat) => seat.status === 3)
    );

    if (allSeats.length > 0) {
      const totalSeats = allSeats.reduce(
        (sum, seat) => sum + (seat.currency?.total || seat.cost || 0),
        0
      );
      // Sabre rechaza la emision con "Total too low in FeeDetails" si el total es 0.
      if (totalSeats > 0) {
        emdItems.push({
          type: 'EMD_SEAT',
          quantity: allSeats.length,
          total: totalSeats,
          description: allSeats.map((seat) => seat.seatCode).join(', '),
        });
      }
    }
    // --- MALETAS EXTRA ---
    // Incluye tanto la maleta extra por pieza (status 'toPay') como el
    // sobrepeso de documentacion (isExtraWeight), que vive en bagStack con
    // un status distinto de 'toPay'. Se alinea con `getProductsPerPassenger`,
    // que usa el mismo criterio para calcular el importe cobrado; de lo
    // contrario el sobrepeso se cobra pero no genera linea DESCRIPCION.
    const allBags = passengers.flatMap((p) =>
      (p.bagStack || []).filter((bag) => bag.status === 'toPay' || bag.isExtraWeight)
    );
    if (allBags.length > 0) {
      const totalBags = allBags.reduce((sum, bag) => sum + (bag.currency?.total || 0), 0);
      // Sabre rechaza la emision con "Total too low in FeeDetails" si el total es 0.
      if (totalBags > 0) {
        emdItems.push({
          type: 'EMD_EXTRA_BAGGAGE',
          quantity: allBags.length,
          total: totalBags,
          description: allBags.map((bag) => bagDescription(bag, NAMES_BAGS)).join(', '),
        });
        weightUnit = 'KG'; // si hay maletas, usamos KG
      }
    }

    // --- CARRY ON ---
    // CVT20-14596: se alinea con `getProductsPerPassenger` (el request que cobra) en dos
    // puntos para que la linea DESCRIPCION no quede vacia aunque el producto se haya cobrado:
    //   1) Filtro `status === 'toPay' || isExtraWeight` -> cubre el carry-on seleccionado
    //      y su +5kg (10kg -> 15kg, que entra con isExtraWeight y status distinto de 'toPay').
    //   2) Se recorre `bagStackCarryon` Y `bagOfferCarryOnStack`: en BASIC/Main Light el
    //      carry-on vive en la oferta, que el pago si recorre y el recibo antes omitia.
    const allCarryons = passengers.flatMap((p) =>
      [...(p.bagStackCarryon || []), ...(p.bagOfferCarryOnStack || [])].filter(
        (bag) => bag.status === 'toPay' || bag.isExtraWeight
      )
    );
    if (allCarryons.length > 0) {
      // Separar carry-on de 10kg y 5kg
      const carryOn10kg = allCarryons.filter((bag) => bag.typeBagWeight === TypeBagsWeight._10KG);
      const carryOn5kg = allCarryons.filter((bag) => bag.typeBagWeight !== TypeBagsWeight._10KG);

      if (carryOn10kg.length > 0) {
        const totalCarryons10kg = carryOn10kg.reduce((sum, bag) => sum + carryOnTotal(bag), 0);
        // Sabre rechaza la emision con "Total too low in FeeDetails" si el total es 0.
        if (totalCarryons10kg > 0) {
          emdItems.push({
            type: 'EMD_CARRY_ON_10KG',
            quantity: carryOn10kg.length,
            total: totalCarryons10kg,
            description: carryOn10kg
              .map((bag) => bagDescription(bag, CARRY_ON_NAMES_BY_WEIGHT))
              .join(', '),
          });
          weightUnit = 'KG';
        }
      }

      if (carryOn5kg.length > 0) {
        const totalCarryons5kg = carryOn5kg.reduce((sum, bag) => sum + carryOnTotal(bag), 0);
        // Sabre rechaza la emision con "Total too low in FeeDetails" si el total es 0.
        if (totalCarryons5kg > 0) {
          emdItems.push({
            type: 'EMD_CARRY_ON_5KG',
            quantity: carryOn5kg.length,
            total: totalCarryons5kg,
            description: carryOn5kg
              .map((bag) => bagDescription(bag, CARRY_ON_NAMES_BY_WEIGHT))
              .join(', '),
          });
          weightUnit = 'KG';
        }
      }
    }

    return {
      weightUnit,
      emdItems,
    };
  }

  /**
   * CVT20-14858: total del seguro (Chubb / seguro de maletas) de la reserva, para inyectarlo
   * en el IMPORTE del recibo. Debe invocarse ANTES de `clearEmittedInsuranceAfterTicket`
   * (CVT20-14665), que vacia los productos de seguro tras emitir y los deja inaccesibles al
   * generar el recibo. Mismos origenes que el request que cobra: bagInsuranceProduct (flujo
   * estandar) y chubbInsuranceProducts (manage-extras), sin duplicar por rficSubcode.
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static getInsuranceTotal(passengers: Passenger[]): number {
    const total = (passengers || []).reduce((sum, p) => {
      const sources: { rficSubcode?: string; currency?: { total?: number } }[] = [];
      if (p?.hasBagInsurance && p?.bagInsuranceSource === 'kiosk' && p?.bagInsuranceProduct) {
        sources.push(p.bagInsuranceProduct);
      }
      (p?.chubbInsuranceProducts || []).forEach((prod) => {
        if (!sources.some((src) => src.rficSubcode === prod.rficSubcode)) {
          sources.push(prod);
        }
      });
      return sum + sources.reduce((acc, prod) => acc + (prod?.currency?.total || 0), 0);
    }, 0);

    return Math.round(total * 100) / 100;
  }
}
